// LLM-API-Helper: Chat und 1-Click-Funktionen gegen das Backend.
// Streams nutzen `fetch` mit ReadableStream (EventSource kann keinen Auth-Header).

import getToken from '../auth/getToken';
import { loadTestHeaders } from '../auth/loadTestAuth';

const API = () => process.env.REACT_APP_BACKEND_URL;
const SCOPE = () => [`${process.env.REACT_APP_CLIENT_ID}/.default`];

async function authHeaders(instance) {
  const token = await getToken(instance, SCOPE());
  if (!token) throw new Error('Keine gültige Authentifizierung');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...loadTestHeaders(),
  };
}

export async function jsonFetch(instance, path, { method = 'GET', body } = {}) {
  const headers = await authHeaders(instance);
  const res = await fetch(`${API()}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

export const listOneClickFunctions = (instance) =>
  jsonFetch(instance, '/api/oneclick');

export const createOneClickFunction = (instance, payload) =>
  jsonFetch(instance, '/api/oneclick', { method: 'POST', body: payload });

export const updateOneClickFunction = (instance, id, payload) =>
  jsonFetch(instance, `/api/oneclick/${id}`, { method: 'PUT', body: payload });

export const deleteOneClickFunction = (instance, id) =>
  jsonFetch(instance, `/api/oneclick/${id}`, { method: 'DELETE' });

export const getOneClickCoverage = (instance, jobId) =>
  jsonFetch(instance, `/api/oneclick/${jobId}/coverage`);

export const getChatHistory = (instance, jobId) =>
  jsonFetch(instance, `/api/chat/${jobId}`);

export const deleteChatHistory = (instance, jobId) =>
  jsonFetch(instance, `/api/chat/${jobId}`, { method: 'DELETE' });

// ---- Streaming-Handler ----

// Fehlertext einer Streaming-Antwort: Das Backend schickt JSON mit "error",
// andere Stellen (Proxy, Server) reinen Text.
export function fehlertextAusAntwort(text, status) {
  try {
    const data = JSON.parse(text);
    if (typeof data?.error === 'string' && data.error) return data.error;
  } catch {
    // kein JSON
  }
  return text || `HTTP ${status}`;
}

async function streamSse({ instance, method, path, body, onEvent, signal }) {
  const headers = await authHeaders(instance);
  const res = await fetch(`${API()}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  if (!res.ok || !res.body) {
    const errText = await res.text().catch(() => '');
    throw new Error(fehlertextAusAntwort(errText, res.status));
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE-Events sind durch "\n\n" getrennt
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';
    for (const block of events) {
      const line = block.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const payload = line.replace(/^data:\s*/, '');
      if (!payload) continue;
      try {
        const obj = JSON.parse(payload);
        onEvent(obj);
      } catch {
        // ignoriere non-JSON
      }
    }
  }
}

export async function streamChat({ instance, jobId, question, onEvent, signal }) {
  return streamSse({
    instance,
    method: 'POST',
    path: `/api/chat/${jobId}`,
    body: { question },
    onEvent,
    signal,
  });
}

export async function streamOneClickRun({ instance, jobId, functionId, onEvent, signal }) {
  return streamSse({
    instance,
    method: 'POST',
    path: `/api/oneclick/${jobId}/run/${functionId}`,
    onEvent,
    signal,
  });
}
